<script setup lang="ts">
    import { computed, toRef } from 'vue'

    import { CapexAccountEditorFormSubmitEventPayload } from '@Quest/Capex/Components/CapexAccountEditor'
    import type { CapexAccountEditorViewModel } from '@Quest/Capex/Components/CapexAccountEditor'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: CapexAccountEditorViewModel
    }>()

    const emit = defineEmits<{
        (e: 'form-submit', payload: CapexAccountEditorFormSubmitEventPayload): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- computed ----------------

    const opened = computed(() => vm.value?.opened ?? false)
    const confirmButtonLabel = computed(() => vm.value.confirmButtonLabel)
    const modalTitle = computed(() => vm.value.modalTitle)

    // writable computeds (replace getter + setter pairs)

    const name = computed<string | null>({
        get: () => vm.value.name,
        set: v => { vm.value.name = v }
    })

    const description = computed<string | null>({
        get: () => vm.value.description,
        set: v => { vm.value.description = v }
    })

    const classification = computed<string | null>({
        get: () => vm.value.classification,
        set: v => { vm.value.classification = v }
    })

    // ---------------- actions ----------------

    function handleFormSubmit()
    {
        emit(
            'form-submit',
            new CapexAccountEditorFormSubmitEventPayload(
                vm.value.id,
                name.value,
                description.value,
                classification.value
            )
        )
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ modalTitle }}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <form id="capex-account-editor-form" @submit.prevent="handleFormSubmit">
                            <div class="form-group">
                                <label>Name:</label>
                                <input type="text" v-model.trim="name" class="form-control" required maxlength="100" minlength="2" />
                            </div>
                            <div class="form-group">
                                <label>Description:</label>
                                <textarea v-model.trim="description" class="form-control" />
                            </div>
                            <div class="form-group">
                                <label for="classification">Depreciation:</label>
                                <select id="classification" v-model="classification" class="form-control">
                                    <option value="OpEx">OpEx</option>
                                    <option value="COGS">COGS</option>
                                    <option value="Other">Other expenses</option>
                                </select>
                            </div>
                        </form>
                    </div>
                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                        <button type="submit" form="capex-account-editor-form" class="btn btn-primary">{{ confirmButtonLabel }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template> 